import {
  Box,
  Button,
  Field,
  IconButton,
  Input,
  InputGroup,
  Stack,
  Text,
} from '@chakra-ui/react'
import { useState } from 'react'
import { LuEye, LuEyeOff } from 'react-icons/lu'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { requestPasswordReset } from '../services/firebaseService'
import { useAuth } from '../contexts/AuthContext'
import { toaster } from '../components/ui/toaster'
import { useTranslation } from '../i18n'

// The webapp login's field (webapp/src/pages/Login.css, .auth__field): small
// uppercase label, black well, hairline border, pink ring on focus.
const labelProps = {
  fontSize: '0.68rem',
  fontWeight: '600',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--mc-text-muted)',
}

const inputProps = {
  size: 'lg',
  bg: 'var(--mc-bg)',
  borderColor: 'var(--crowds-line)',
  color: 'var(--mc-text)',
  fontSize: '1rem',
  _focus: { borderColor: 'var(--mc-focus)', outline: '2px solid var(--mc-focus)', outlineOffset: '-3px' },
}

export default function LoginPage() {
  const { login, user } = useAuth()
  const { t, lang } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const navigate = useNavigate()
  const location = useLocation()

  const from = location.state?.from?.pathname || '/'

  // Already signed in: the form would just re-authenticate the same account and
  // leave the user staring at it. Send them on, so an account without admin
  // rights gets told so instead of a login that silently appears to work.
  if (user && !user.isAnonymous) {
    return <Navigate to={from} replace />
  }

  const validate = () => {
    const errs = {}
    if (!email) errs.email = t('login.emailRequired')
    else if (!/\S+@\S+\.\S+/.test(email)) errs.email = t('login.emailInvalid')
    if (!password) errs.password = t('login.passwordRequired')
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    setErrors({})
    try {
      await login(email, password)
      navigate(from, { replace: true })
    } catch (err) {
      toaster.create({
        title: t('login.loginFailed'),
        description: err.message,
        status: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    if (!email) {
      toaster.create({
        title: t('login.enterEmailFirst'),
        description: t('login.enterEmailFirstDesc'),
        status: 'warning',
      })
      return
    }
    try {
      // Sent by our function in the dashboard's language. Without a continue
      // URL Firebase's reset page ends on a dead end, so point it back at the
      // page they were after; the route guard sends them through this login.
      await requestPasswordReset({
        email,
        lang,
        continueUrl: `${window.location.origin}${from}`,
      })
      toaster.create({
        title: t('login.resetEmailSent'),
        description: t('login.resetEmailSentDesc', { email }),
        status: 'success',
      })
    } catch (err) {
      toaster.create({
        title: t('login.resetEmailFailed'),
        description: err.code === 'functions/resource-exhausted'
          ? t('login.resetTooMany')
          : err.message,
        status: 'error',
      })
    }
  }

  return (
    <Box
      minH="calc(100vh - 8rem)"
      display="grid"
      placeItems="center"
      position="relative"
    >
      {/* The webapp login's halo, centred behind the panel. Fixed, like the
          body's grid, so it stays put however the page is scrolled. */}
      <Box
        position="fixed"
        inset={0}
        pointerEvents="none"
        bgImage="radial-gradient(78% 46% at 50% 42%, rgba(254, 106, 190, 0.22), rgba(254, 106, 190, 0) 72%)"
      />

      <Box position="relative" w="100%" maxW="360px">
        <img
          src="/CROWDS_logo-multicolor.svg"
          alt="CROWDS"
          style={{ display: 'block', width: '100%', height: 'auto', marginBottom: '1rem' }}
        />
        <Text textAlign="center" fontSize="0.9rem" color="var(--mc-text-muted)" mb="0.9rem">
          {t('login.subtitle')}
        </Text>

        <form onSubmit={handleSubmit} noValidate>
          {/* The extra 5px on two sides is room for the submit's offset shadow. */}
          <Stack
            gap="0.85rem"
            p="1.1rem"
            pr="calc(1.1rem + 5px)"
            pb="calc(1.1rem + 5px)"
            bg="var(--mc-bg-surface)"
            border="1px solid var(--crowds-line)"
            borderRadius="12px"
          >
            <Field.Root invalid={!!errors.email} gap="0.4rem">
              <Field.Label {...labelProps}>{t('login.email')}</Field.Label>
              <Input
                {...inputProps}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: undefined })) }}
                type="email"
                autoComplete="email"
                autoFocus
                placeholder={t('login.emailPlaceholder')}
              />
              {errors.email && <Field.ErrorText color="var(--mc-red)" fontSize="xs">{errors.email}</Field.ErrorText>}
            </Field.Root>

            <Field.Root invalid={!!errors.password} gap="0.4rem">
              <Field.Label {...labelProps}>{t('login.password')}</Field.Label>
              <InputGroup
                endElement={
                  <IconButton
                    variant="plain"
                    size="sm"
                    color="var(--mc-text-muted)"
                    aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <LuEyeOff /> : <LuEye />}
                  </IconButton>
                }
              >
                <Input
                  {...inputProps}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setErrors(prev => ({ ...prev, password: undefined })) }}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder={t('login.passwordPlaceholder')}
                />
              </InputGroup>
              {errors.password && <Field.ErrorText color="var(--mc-red)" fontSize="xs">{errors.password}</Field.ErrorText>}
            </Field.Root>

            <Button type="submit" size="lg" mt="0.15rem" colorPalette="brand" loading={loading}>
              {t('login.signIn')}
            </Button>
          </Stack>
        </form>

        <Box textAlign="center" mt="0.9rem">
          <Button
            variant="plain"
            size="xs"
            color="var(--mc-text-muted)"
            fontWeight="600"
            letterSpacing="0.08em"
            textTransform="uppercase"
            _hover={{ color: 'var(--mc-text)' }}
            onClick={handleForgotPassword}
          >
            {t('login.forgotPassword')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
